import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { M365User } from "../types";

interface SkuReport {
  sku_id: string;
  sku_part_number: string;
  total: number;
  consumed: number;
  available: number;
}

/**
 * Per-user M365 license management. Lists the user's current SKUs (with
 * remove), and offers the tenant's SKUs (with available counts) to assign.
 * Backed by /api/v1/m365/users/{id}/{licenses,assign-license,remove-license}
 * and /api/v1/m365/{customer_id}/license-report.
 */
export default function M365LicenseModal({
  user,
  skuDisplay,
  onClose,
  onChanged,
}: {
  user: M365User;
  skuDisplay: (part: string) => string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [current, setCurrent] = useState<string[]>([]);
  const [report, setReport] = useState<SkuReport[]>([]);
  const [selSku, setSelSku] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    api
      .get(`/api/v1/m365/users/${user.id}/licenses`)
      .then((r) => setCurrent(r.data.map((x: { sku_id: string }) => x.sku_id)))
      .catch(() => setCurrent([]));
    if (user.customer_id != null) {
      api
        .get(`/api/v1/m365/${user.customer_id}/license-report`)
        .then((r) => setReport(r.data))
        .catch((e) => setError(e.response?.data?.detail || "Couldn't load tenant SKUs"));
    }
  }, [user.id, user.customer_id]);

  useEffect(() => {
    load();
  }, [load]);

  const partFor = useMemo(() => {
    const m: Record<string, string> = {};
    report.forEach((s) => {
      m[s.sku_id] = s.sku_part_number;
    });
    return m;
  }, [report]);

  const assign = async () => {
    if (!selSku) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await api.post(`/api/v1/m365/users/${user.id}/assign-license`, {
        sku_id: selSku,
      });
      // Microsoft refuses to license an account with no usage location, so the
      // server sets one from the tenant's country and carries on. SAY SO —
      // changing someone's account attribute and not mentioning it is how a
      // tool stops being trustworthy, even when the change is the right one.
      if (r.data?.usage_location_set) {
        setNotice(
          `Licence assigned. This account had no usage location, which Microsoft requires ` +
            `before any licence will attach, so it was set to ${r.data.usage_location_set} ` +
            `to match the tenant.`,
        );
      }
      setSelSku("");
      load();
      onChanged();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (skuId: string) => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/v1/m365/users/${user.id}/remove-license`, { sku_id: skuId });
      load();
      onChanged();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  const assignable = report.filter((s) => !current.includes(s.sku_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Manage licenses</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{user.upn}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-sm">
            ✕
          </button>
        </div>

        {error && (
          <p className="text-xs text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
            {error}
          </p>
        )}

        {notice && (
          <p className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 px-3 py-2 rounded-lg mb-3">
            {notice}
          </p>
        )}

        <div className="mb-4">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Assigned</div>
          {current.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">No licenses assigned.</p>
          ) : (
            <div className="space-y-1">
              {current.map((sid) => (
                <div
                  key={sid}
                  className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5"
                >
                  <span className="text-sm text-gray-800 dark:text-gray-100">
                    {skuDisplay(partFor[sid] || sid)}
                  </span>
                  <button
                    onClick={() => remove(sid)}
                    disabled={busy}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Assign a license</label>
            <select
              value={selSku}
              onChange={(e) => setSelSku(e.target.value)}
              className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="">Select a SKU…</option>
              {assignable.map((s) => (
                <option key={s.sku_id} value={s.sku_id} disabled={s.available <= 0}>
                  {skuDisplay(s.sku_part_number)} ({s.available} available)
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={assign}
            disabled={busy || !selSku}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}
